import React from 'react';
import {
  View,
  ViewProps,
  ViewStyle,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useTheme } from '../../hooks/useTheme';

interface CardProps extends ViewProps {
  variant?: 'default' | 'elevated' | 'outlined' | 'filled';
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  margin?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  shadow?: boolean;
  pressable?: boolean;
  onPress?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}

/**
 * Versatile Card component with multiple styling options
 */
const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'md',
  margin = 'none',
  radius = 'md',
  shadow = false,
  pressable = false,
  onPress,
  disabled = false,
  style,
  children,
  ...props
}) => {
  const { colors } = useTheme();

  const getCardStyles = () => {
    const baseStyles: ViewStyle[] = [styles.card];

    // Background and border styles based on variant
    switch (variant) {
      case 'elevated':
        baseStyles.push(styles.elevated);
        baseStyles.push({
          backgroundColor: colors.card,
          ...Platform.select({
            ios: {
              shadowColor: colors.cardText,
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
            },
            android: {
              elevation: 4,
            },
          }),
        });
        break;
      case 'outlined':
        baseStyles.push(styles.outlined);
        baseStyles.push({
          backgroundColor: colors.card,
          borderColor: colors.border,
        });
        break;
      case 'filled':
        baseStyles.push(styles.filled);
        baseStyles.push({
          backgroundColor: colors.border,
        });
        break;
      case 'default':
      default:
        baseStyles.push({ backgroundColor: colors.card });
    }

    // Additional shadow
    if (shadow && variant !== 'elevated') {
      baseStyles.push({
        ...Platform.select({
          ios: {
            shadowColor: colors.cardText,
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.05,
            shadowRadius: 2,
          },
          android: {
            elevation: 2,
          },
        }),
      });
    }

    return baseStyles;
  };

  const getPadding = () => {
    switch (padding) {
      case 'none':
        return 0;
      case 'xs':
        return 8;
      case 'sm':
        return 12;
      case 'md':
        return 16;
      case 'lg':
        return 20;
      case 'xl':
        return 24;
      default:
        return 16;
    }
  };

  const getMargin = () => {
    switch (margin) {
      case 'none':
        return 0;
      case 'xs':
        return 4;
      case 'sm':
        return 8;
      case 'md':
        return 12;
      case 'lg':
        return 16;
      case 'xl':
        return 20;
      default:
        return 0;
    }
  };

  const getBorderRadius = () => {
    switch (radius) {
      case 'none':
        return 0;
      case 'sm':
        return 6;
      case 'md':
        return 8;
      case 'lg':
        return 12;
      case 'xl':
        return 16;
      case 'full':
        return 9999;
      default:
        return 8;
    }
  };

  const cardStyle = [
    ...getCardStyles(),
    {
      padding: getPadding(),
      margin: getMargin(),
      borderRadius: getBorderRadius(),
    },
    disabled && styles.disabled,
    style,
  ];

  if (pressable || onPress) {
    return (
      <TouchableOpacity
        style={cardStyle}
        onPress={onPress}
        disabled={disabled}
        activeOpacity={0.8}
        {...props}
      >
        {children}
      </TouchableOpacity>
    );
  }

  return (
    <View style={cardStyle} {...props}>
      {children}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    // Base card styles
  },
  elevated: {
    // Additional styles for elevated variant
  },
  outlined: {
    borderWidth: 1,
  },
  filled: {
    // Additional styles for filled variant
  },
  disabled: {
    opacity: 0.6,
  },
});

export default Card;
